import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiPlus, FiEdit2, FiTrash2, FiX, FiSave, FiAlertCircle, FiArrowLeft, FiChevronUp, FiChevronDown } = FiIcons;

const CountryManager = ({ countries, onAddCountry, onEditCountry, onDeleteCountry, onBack }) => {
  const [showForm, setShowForm] = useState(false);
  const [editingCountry, setEditingCountry] = useState(null);
  const [formData, setFormData] = useState({ code: '', name: '', flag: '', currency: '' });
  const [errors, setErrors] = useState({});
  const [showScrollUp, setShowScrollUp] = useState(false);
  const [showScrollDown, setShowScrollDown] = useState(true);
  const contentRef = useRef(null);

  useEffect(() => {
    if (editingCountry) {
      setFormData({
        code: editingCountry.code || '',
        name: editingCountry.name || '',
        flag: editingCountry.flag || '',
        currency: editingCountry.currency || ''
      });
    } else {
      setFormData({ code: '', name: '', flag: '', currency: '' });
    }
  }, [editingCountry]);

  // Scroll position detection
  useEffect(() => {
    const handleScroll = () => {
      if (!contentRef.current) return;
      
      const { scrollTop, scrollHeight, clientHeight } = contentRef.current;
      setShowScrollUp(scrollTop > 20);
      setShowScrollDown(scrollTop + clientHeight < scrollHeight - 20);
    };

    const contentElement = contentRef.current;
    if (contentElement) {
      contentElement.addEventListener('scroll', handleScroll);
      // Initial check
      handleScroll();
    }

    return () => {
      if (contentElement) {
        contentElement.removeEventListener('scroll', handleScroll);
      }
    };
  }, [showForm]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    // Clear error when field is edited
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: null
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.code.trim()) newErrors.code = 'Country code is required';
    else if (formData.code.length !== 2) newErrors.code = 'Country code must be 2 characters';
    else if (!/^[A-Z]+$/.test(formData.code)) newErrors.code = 'Country code must be uppercase letters';

    if (!formData.name.trim()) newErrors.name = 'Country name is required';
    if (!formData.flag.trim()) newErrors.flag = 'Country flag emoji is required';
    if (!formData.currency.trim()) newErrors.currency = 'Currency code is required';

    // Check for duplicate country code (if adding new)
    if (!editingCountry && countries.some(c => c.code === formData.code)) {
      newErrors.code = 'This country code already exists';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (validateForm()) {
      if (editingCountry) {
        onEditCountry({
          ...formData,
          originalCode: editingCountry.code // Keep track of original code for proper updating
        });
      } else {
        onAddCountry(formData);
      }
      setShowForm(false);
      setEditingCountry(null);
    }
  };

  const handleDelete = (country) => {
    if (window.confirm(`Are you sure you want to delete ${country.name}? This will also delete all associated coupons.`)) {
      onDeleteCountry(country.code);
    }
  };

  const handleAddNew = () => {
    setEditingCountry(null);
    setShowForm(true);
  };

  const handleEdit = (country) => {
    setEditingCountry(country);
    setShowForm(true);
  };

  const handleCancel = () => {
    setShowForm(false);
    setEditingCountry(null);
    setErrors({});
  };

  const scrollToTop = () => {
    if (contentRef.current) {
      contentRef.current.scrollTo({
        top: 0,
        behavior: 'smooth'
      });
    }
  };

  const scrollToBottom = () => {
    if (contentRef.current) {
      contentRef.current.scrollTo({
        top: contentRef.current.scrollHeight,
        behavior: 'smooth'
      });
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="flex flex-col h-full"
    >
      <div className="flex items-center mb-5">
        <button
          onClick={onBack}
          className="mr-3 text-gray-500 hover:text-gray-700 transition-colors"
        >
          <SafeIcon icon={FiArrowLeft} className="text-lg" />
        </button>
        <h2 className="text-xl font-bold text-gray-800">Manage Countries</h2>
      </div>

      <div className="relative flex-1 overflow-hidden">
        {showScrollUp && (
          <button 
            onClick={scrollToTop}
            className="absolute top-0 left-1/2 transform -translate-x-1/2 z-10 bg-white bg-opacity-80 rounded-full p-1 shadow-md hover:bg-opacity-100 transition-all"
          >
            <SafeIcon icon={FiChevronUp} className="text-gray-600" />
          </button>
        )}
        
        {showScrollDown && (
          <button 
            onClick={scrollToBottom}
            className="absolute bottom-0 left-1/2 transform -translate-x-1/2 z-10 bg-white bg-opacity-80 rounded-full p-1 shadow-md hover:bg-opacity-100 transition-all"
          >
            <SafeIcon icon={FiChevronDown} className="text-gray-600" />
          </button>
        )}
        
        <AnimatePresence mode="wait">
          {showForm ? (
            <motion.form
              key="countryForm"
              ref={contentRef}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onSubmit={handleSubmit}
              className="bg-white rounded-lg p-5 overflow-auto max-h-[calc(90vh-120px)]"
            >
              <h3 className="text-lg font-semibold text-gray-800 mb-4">
                {editingCountry ? 'Edit Country' : 'Add New Country'}
              </h3>

              <div className="grid grid-cols-2 gap-4 mb-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Country Code * <span className="text-xs text-gray-500">(2-letter ISO code)</span>
                  </label>
                  <input
                    type="text"
                    name="code"
                    value={formData.code}
                    onChange={handleChange}
                    maxLength="2"
                    placeholder="e.g., US, UK, JP"
                    className={`w-full p-2 border rounded-lg ${errors.code ? 'border-red-500' : 'border-gray-300'}`}
                    disabled={!!editingCountry} // Disable editing code for existing countries
                  />
                  {errors.code && (
                    <p className="mt-1 text-sm text-red-600 flex items-center">
                      <SafeIcon icon={FiAlertCircle} className="mr-1" />
                      {errors.code}
                    </p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Currency Code *
                  </label>
                  <input
                    type="text"
                    name="currency"
                    value={formData.currency}
                    onChange={handleChange}
                    placeholder="e.g., USD, EUR, JPY"
                    className={`w-full p-2 border rounded-lg ${errors.currency ? 'border-red-500' : 'border-gray-300'}`}
                  />
                  {errors.currency && (
                    <p className="mt-1 text-sm text-red-600 flex items-center">
                      <SafeIcon icon={FiAlertCircle} className="mr-1" />
                      {errors.currency}
                    </p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Country Name *
                  </label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    placeholder="e.g., United States, Japan"
                    className={`w-full p-2 border rounded-lg ${errors.name ? 'border-red-500' : 'border-gray-300'}`}
                  />
                  {errors.name && (
                    <p className="mt-1 text-sm text-red-600 flex items-center">
                      <SafeIcon icon={FiAlertCircle} className="mr-1" />
                      {errors.name}
                    </p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Flag Emoji *
                  </label>
                  <input
                    type="text"
                    name="flag"
                    value={formData.flag}
                    onChange={handleChange}
                    placeholder="e.g., 🇺🇸, 🇬🇧, 🇯🇵"
                    className={`w-full p-2 border rounded-lg ${errors.flag ? 'border-red-500' : 'border-gray-300'}`}
                  />
                  {errors.flag && (
                    <p className="mt-1 text-sm text-red-600 flex items-center">
                      <SafeIcon icon={FiAlertCircle} className="mr-1" />
                      {errors.flag}
                    </p>
                  )}
                  <p className="mt-1 text-xs text-gray-500">
                    Tip: You can copy-paste flag emojis from the web
                  </p>
                </div>
              </div>

              <div className="flex justify-end space-x-3">
                <button
                  type="button"
                  onClick={handleCancel}
                  className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors flex items-center space-x-1"
                >
                  <SafeIcon icon={FiX} />
                  <span>Cancel</span>
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded-lg transition-colors flex items-center space-x-1"
                >
                  <SafeIcon icon={FiSave} />
                  <span>{editingCountry ? 'Update' : 'Save'}</span>
                </button>
              </div>
            </motion.form>
          ) : (
            <motion.div
              key="countryList"
              ref={contentRef}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="flex flex-col overflow-auto max-h-[calc(90vh-120px)]"
            >
              <button
                onClick={handleAddNew}
                className="mb-4 py-2 flex items-center justify-center space-x-2 bg-blue-500 hover:bg-blue-600 text-white font-medium rounded-lg transition-colors"
              >
                <SafeIcon icon={FiPlus} />
                <span>Add New Country</span>
              </button>

              <div className="flex-1 overflow-auto">
                {countries.length > 0 ? (
                  <div className="grid gap-3">
                    {countries.map((country) => (
                      <div
                        key={country.code}
                        className="p-4 border rounded-xl hover:shadow-md transition-shadow bg-white flex items-center justify-between"
                      >
                        <div className="flex items-center space-x-3">
                          <div className="text-3xl">{country.flag}</div>
                          <div>
                            <h3 className="font-medium text-gray-900">{country.name}</h3>
                            <div className="flex items-center space-x-3 text-sm text-gray-500">
                              <span className="font-mono bg-gray-100 px-2 py-0.5 rounded">{country.code}</span>
                              <span>{country.currency}</span>
                            </div>
                          </div>
                        </div>

                        <div className="flex items-center space-x-2">
                          <button
                            onClick={() => handleEdit(country)}
                            className="p-2 text-blue-600 hover:bg-blue-50 rounded-full transition-colors"
                            title="Edit country"
                          >
                            <SafeIcon icon={FiEdit2} />
                          </button>
                          <button
                            onClick={() => handleDelete(country)}
                            className="p-2 text-red-600 hover:bg-red-50 rounded-full transition-colors"
                            title="Delete country"
                            disabled={countries.length <= 1}
                          >
                            <SafeIcon icon={FiTrash2} className={countries.length <= 1 ? "text-gray-400" : ""} />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="flex flex-col items-center justify-center h-64 p-5">
                    <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-3">
                      <SafeIcon icon={FiAlertCircle} className="text-gray-400 text-xl" />
                    </div>
                    <h3 className="text-lg font-medium text-gray-700 mb-1">No countries found</h3>
                    <p className="text-gray-500 text-center">
                      Add your first country to get started
                    </p>
                  </div>
                )}
              </div>

              {countries.length <= 1 && countries.length > 0 && (
                <div className="mt-4 p-3 bg-yellow-50 text-yellow-700 rounded-lg flex items-center">
                  <SafeIcon icon={FiAlertCircle} className="mr-2 flex-shrink-0" />
                  <p className="text-sm">
                    You must have at least one country in the system. Delete is disabled when only one country remains.
                  </p>
                </div>
              )}
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </motion.div>
  );
};

export default CountryManager;